'use client'

import * as React from 'react'
import { cn } from '../../../lib/utils'
import { Icon } from '../Icon'
import { Select } from '../Select'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../Collapsible'
import { LiquidInput, type LiquidContextField } from '../LiquidInput'
import { buildFieldSelectItems } from './fieldCompatibility'
import {
  normalizeEntityFieldOptionsList,
  resolveCrmFieldOptionsForMapping,
  suggestValueMap,
} from './fieldMappingOptions'
import type {
  CrmMapFieldDirection,
  CrmMapFieldMappingView,
  EntityFieldDef,
  WorkplaceFieldOption,
} from './types'

export type FieldMappingDirection = CrmMapFieldDirection

export type FieldMappingRowProps = {
  field: EntityFieldDef
  mapping: CrmMapFieldMappingView | undefined
  fields: WorkplaceFieldOption[]
  targetField?: WorkplaceFieldOption
  contextFields: LiquidContextField[]
  isDisabled?: boolean
  /** When true, expand the enum value-map section by default */
  forceValueMapOpen?: boolean
  /** Hide label/handle column (e.g. when opened from an inline chip trigger) */
  hideFieldLabel?: boolean
  /** Show direction controls (both/inbound/outbound) */
  showDirectionControls?: boolean
  onChange: (mapping: CrmMapFieldMappingView | null) => void
}

function defaultSourceLiquid(entityPath: string): string {
  return `{{ ${entityPath} }}`
}

const DirectionToggle = ({
  direction,
  onChange,
  disabled,
  compact,
}: {
  direction: FieldMappingDirection
  onChange: (dir: FieldMappingDirection) => void
  disabled?: boolean
  compact?: boolean
}) => {
  const options: { value: FieldMappingDirection; label: string; icon: 'ArrowLeftRight' | 'ArrowDown' | 'ArrowUp' }[] = [
    { value: 'both', label: 'Both', icon: 'ArrowLeftRight' },
    { value: 'inbound', label: 'In', icon: 'ArrowDown' },
    { value: 'outbound', label: 'Out', icon: 'ArrowUp' },
  ]

  return (
    <div className={cn('flex items-center rounded-md border bg-muted/30', compact ? 'p-0.5' : 'p-0.5')}>
      {options.map((opt) => (
        <button
          key={opt.value}
          type="button"
          title={`Sync ${opt.value === 'both' ? 'both ways' : opt.value === 'inbound' ? 'from external only' : 'to external only'}`}
          className={cn(
            'rounded transition-colors flex items-center gap-0.5',
            compact ? 'px-1 py-0.5 text-[9px]' : 'px-1.5 py-0.5 text-[10px]',
            direction === opt.value
              ? 'bg-background shadow-sm text-foreground'
              : 'text-muted-foreground hover:text-foreground',
          )}
          disabled={disabled}
          onClick={() => onChange(opt.value)}
        >
          <Icon name={opt.icon} className={compact ? 'h-2.5 w-2.5' : 'h-3 w-3'} />
          {!compact && <span>{opt.label}</span>}
        </button>
      ))}
    </div>
  )
}

export const FieldMappingRow: React.FC<FieldMappingRowProps> = ({
  field,
  mapping,
  fields,
  targetField,
  contextFields,
  isDisabled = false,
  forceValueMapOpen = false,
  hideFieldLabel = false,
  showDirectionControls = false,
  onChange,
}) => {
  const mode = mapping?.mode ?? 'field'
  const direction: FieldMappingDirection = mapping?.direction ?? 'both'
  const sourceLiquid =
    mapping?.sourceLiquid ?? defaultSourceLiquid(field.handle)
  const hasSourceOptions = Boolean(field.options?.length)
  const sourceOptions = normalizeEntityFieldOptionsList(field.options)

  const fieldItems = React.useMemo(
    () =>
      buildFieldSelectItems({
        fields,
        entityType: field.type,
        entityHasOptions: hasSourceOptions,
        selectedId: mapping?.targetFieldId,
      }),
    [fields, field.type, hasSourceOptions, mapping?.targetFieldId],
  )

  const setMapping = (patch: Partial<CrmMapFieldMappingView>) => {
    if (!mapping?.targetFieldId && !patch.targetFieldId) {
      if (patch.targetFieldId) {
        onChange({
          entityPath: field.handle,
          targetFieldId: patch.targetFieldId,
          mode: patch.mode ?? 'field',
          sourceLiquid: patch.sourceLiquid ?? defaultSourceLiquid(field.handle),
          valueMap: patch.valueMap,
          direction: patch.direction,
        })
      }
      return
    }

    const base = mapping ?? {
      entityPath: field.handle,
      targetFieldId: '',
      mode: 'field' as const,
      sourceLiquid: defaultSourceLiquid(field.handle),
    }

    onChange({ ...base, ...patch })
  }
  
  const setDirection = (dir: FieldMappingDirection) => {
    if (!mapping?.targetFieldId) return
    setMapping({ direction: dir })
  }

  const clearMapping = () => onChange(null)

  const setMode = (nextMode: 'field' | 'liquid') => {
    if (!mapping?.targetFieldId) return
    setMapping({
      mode: nextMode,
      sourceLiquid: mapping.sourceLiquid ?? defaultSourceLiquid(field.handle),
    })
  }

  const crmOptions = resolveCrmFieldOptionsForMapping(targetField, sourceOptions)
  const showEnumMap =
    Boolean(mapping?.targetFieldId) &&
    sourceOptions.length > 0 &&
    crmOptions.length > 0
  const showEnumMapHint =
    sourceOptions.length > 0 &&
    Boolean(mapping?.targetFieldId) &&
    crmOptions.length === 0

  // Auto-suggest value mappings for source options without an explicit entry.
  React.useEffect(() => {
    if (!mapping?.targetFieldId || !showEnumMap) return

    const current = mapping.valueMap ?? {}
    const next = suggestValueMap(sourceOptions, crmOptions, current)
    if (!next) return

    const changed = sourceOptions.some((source) => {
      if (Object.prototype.hasOwnProperty.call(current, source.value)) return false
      return next[source.value] !== undefined
    })
    if (!changed) return

    setMapping({ valueMap: next })
  }, [mapping?.targetFieldId, showEnumMap, crmOptions, sourceOptions])

  const modeToggle = (
    <div
      className={cn(
        'flex shrink-0 items-center rounded-md border bg-muted/30',
        hideFieldLabel ? 'p-0.5' : 'p-0.5',
      )}
    >
      <button
        type="button"
        title="Map to CRM field"
        aria-label="Map to CRM field"
        className={cn(
          'rounded transition-colors',
          hideFieldLabel ? 'px-1.5 py-0.5' : 'rounded px-2 py-1',
          mode === 'field'
            ? 'bg-background shadow-sm text-foreground'
            : 'text-muted-foreground hover:text-foreground',
        )}
        disabled={isDisabled || !mapping?.targetFieldId}
        onClick={() => setMode('field')}
      >
        <Icon
          name="Link2"
          className={hideFieldLabel ? 'h-3 w-3' : 'h-3.5 w-3.5'}
        />
      </button>
      <button
        type="button"
        title="Liquid expression"
        aria-label="Liquid expression"
        className={cn(
          'rounded transition-colors',
          hideFieldLabel ? 'px-1.5 py-0.5' : 'rounded px-2 py-1',
          mode === 'liquid'
            ? 'bg-background shadow-sm text-foreground'
            : 'text-muted-foreground hover:text-foreground',
        )}
        disabled={isDisabled || !mapping?.targetFieldId}
        onClick={() => setMode('liquid')}
      >
        <Icon
          name="Braces"
          className={hideFieldLabel ? 'h-3 w-3' : 'h-3.5 w-3.5'}
        />
      </button>
    </div>
  )

  const valueControl =
    mode === 'liquid' && mapping?.targetFieldId ? (
      <LiquidInput
        id={`crm-map-liquid-${field.handle}`}
        variant="inline"
        isMultiLineEnabled={hideFieldLabel}
        value={sourceLiquid}
        onChange={(next) => setMapping({ mode: 'liquid', sourceLiquid: next })}
        contextFields={contextFields}
        disabled={isDisabled}
        placeholder={defaultSourceLiquid(field.handle)}
      />
    ) : (
      <Select
        id={`crm-map-field-${field.handle}`}
        placeholder="Not connected"
        value={mapping?.targetFieldId ?? ''}
        items={fieldItems}
        isDisabled={isDisabled}
        handleChange={(v) => {
          if (!v) {
            clearMapping()
            return
          }
          const nextTargetField = fields.find((field) => field.id === v)
          const nextCrmOptions = resolveCrmFieldOptionsForMapping(
            nextTargetField,
            sourceOptions,
          )
          onChange({
            entityPath: field.handle,
            targetFieldId: v,
            mode: mapping?.mode ?? 'field',
            sourceLiquid:
              mapping?.sourceLiquid ?? defaultSourceLiquid(field.handle),
            valueMap: suggestValueMap(sourceOptions, nextCrmOptions),
          })
        }}
      />
    )

  return (
    <div
      className={cn(
        'space-y-2',
        !hideFieldLabel && 'rounded-xl border bg-card px-3 py-2.5',
      )}
    >
      {hideFieldLabel ? (
        <div className="flex min-w-0 items-center gap-2">
          {showDirectionControls && (
            <DirectionToggle
              direction={direction}
              onChange={setDirection}
              disabled={isDisabled || !mapping?.targetFieldId}
              compact
            />
          )}
          {modeToggle}
          <div className="min-w-0 flex-1">{valueControl}</div>
        </div>
      ) : (
        <div className={cn(
          'grid items-center gap-3',
          showDirectionControls
            ? 'grid-cols-[minmax(0,10rem)_auto_auto_minmax(0,1fr)]'
            : 'grid-cols-[minmax(0,10rem)_auto_minmax(0,1fr)]',
        )}>
          <div className="min-w-0">
            <p className="text-sm font-medium text-foreground truncate">
              {field.label}
              {field.required ? (
                <span className="ml-1 text-destructive">*</span>
              ) : null}
            </p>
            <p className="font-mono text-[11px] text-muted-foreground truncate">
              {field.handle}
            </p>
          </div>
          {showDirectionControls && (
            <DirectionToggle
              direction={direction}
              onChange={setDirection}
              disabled={isDisabled || !mapping?.targetFieldId}
            />
          )}
          {modeToggle}
          <div className="min-w-0">{valueControl}</div>
        </div>
      )}

      {showEnumMapHint && (
        <div className="rounded-lg border border-dashed bg-muted/15 px-2.5 py-2 text-[11px] text-muted-foreground">
          {!mapping?.targetFieldId
            ? 'Choose a string CRM field with options to map enum values.'
            : 'The selected CRM field has no options. Pick a string field with options.'}
          <ul className="mt-1.5 space-y-0.5">
            {sourceOptions.map((opt) => (
              <li key={opt.value} className="truncate">
                {opt.label}
              </li>
            ))}
          </ul>
        </div>
      )}

      {showEnumMap && mapping && (
        <Collapsible
          defaultOpen={
            forceValueMapOpen ||
            Boolean(Object.keys(mapping.valueMap ?? {}).length)
          }
          className="group"
        >
          <CollapsibleTrigger className="flex w-full items-center gap-2 rounded-md px-1 py-1 text-xs text-muted-foreground hover:text-foreground">
            <Icon
              name="ChevronRight"
              className="h-3 w-3 transition-transform group-data-[state=open]:rotate-90"
            />
            Value map
            {Object.keys(mapping.valueMap ?? {}).length > 0 && (
              <span className="text-[10px]">
                ({Object.keys(mapping.valueMap ?? {}).length} mapped)
              </span>
            )}
          </CollapsibleTrigger>
          <CollapsibleContent className="mt-2 space-y-1.5 rounded-lg border bg-muted/15 p-2.5">
            <p className="text-[11px] text-muted-foreground">
              Map each source option to a CRM option.
            </p>
            {sourceOptions.map((opt) => {
              const source = opt.value
              const OMIT_VALUE = '__omit__'
              return (
                <div
                  key={source}
                  className="grid grid-cols-[minmax(0,10rem)_minmax(0,1fr)] gap-3 items-center"
                >
                  <span className="truncate text-xs text-muted-foreground">
                    {opt.label}
                  </span>
                  <Select
                    id={`crm-map-value-${field.handle}-${source}`}
                    placeholder="—"
                    value={mapping.valueMap?.[source] ?? OMIT_VALUE}
                    items={[
                      { value: OMIT_VALUE, label: '— Omit —' },
                      ...crmOptions.map((o) => ({
                        value: o.value,
                        label: o.label,
                      })),
                    ]}
                    handleChange={(v) => {
                      const valueMap = { ...(mapping.valueMap ?? {}) }
                      if (!v || v === OMIT_VALUE) {
                        delete valueMap[source]
                      } else {
                        valueMap[source] = v
                      }
                      setMapping({
                        valueMap:
                          Object.keys(valueMap).length > 0
                            ? valueMap
                            : undefined,
                      })
                    }}
                  />
                </div>
              )
            })}
          </CollapsibleContent>
        </Collapsible>
      )}
    </div>
  )
}

export default FieldMappingRow
